import type { MatchSummary } from '@sol-pool/engine-rules'
import { t } from '@/i18n'

/**
 * O placar da partida: o que vai escrito e onde cada coisa cabe.
 *
 * Nasceu de um pedido dos jogadores: "placar bem claro — a galera tira foto e
 * posta depois". O HUD dizia "Vez do jogador 2" em 14px num canto, "4 na mesa"
 * no outro, e os grupos ficavam escritos em 10px sobre a madeira. Numa foto de
 * celular nada disso se lia — e foto de celular é como o resultado circula.
 *
 * Aqui moram as duas decisões que dão para errar sem ver:
 *
 *   - DADOS: como cada jogador é chamado, que número aparece grande (bolas que
 *     faltam no 8-Ball, pontos na sinuca), quem está em destaque. Igual nas
 *     duas modalidades e nos dois modos (treino e rede), por construção.
 *   - LAYOUT: dois cartões lado a lado na faixa acima da mesa, em qualquer
 *     tela. No desktop sobra um centro para o status; num celular em pé os
 *     cartões tomam a largura e o status desce para baixo da mesa; num
 *     celular deitado a faixa encolhe e o cartão vira uma linha só.
 *
 * Módulo puro, sem canvas: é o que permite provar num teste que os cartões
 * nunca se sobrepõem nem saem da tela — que é o que um screenshot só não prova.
 */

// ------------------------------------------------------------------ dados

export type JogadorDoPlacar = {
  /** Nome curto: "Você"/"Adversário" na rede, "Jogador 1"/"Jogador 2" no treino. */
  nome: string
  /** Segunda linha discreta: o endereço da carteira, abreviado. Nulo no treino. */
  sub: string | null
  /** As bolas que ainda faltam a este jogador. Nulo quando não há grupo (sinuca, mesa aberta). */
  bolas: number[] | null
  /** O número grande: bolas que faltam ou pontos. Vazio quando ainda não há o que contar. */
  numero: string
  /** O que o número significa ("faltam", "pontos", "mesa aberta"). */
  legenda: string
  /** Está na vez — é quem fica em destaque. Nunca com a partida terminada. */
  naVez: boolean
  venceu: boolean
  /** É quem está nesta tela: na rede o "Você"; no treino, os dois (jogam na mesma tela). */
  voce: boolean
}

/** Endereço em forma curta, do jeito que o resto da interface mostra. */
export const enderecoCurto = (a: string): string =>
  a.length <= 11 ? a : `${a.slice(0, 4)}…${a.slice(-4)}`

/**
 * O que vai escrito em cada cartão.
 *
 * `net` nulo é o treino em hotseat: os dois jogam na mesma tela, e chamar um
 * deles de "você" seria mentira metade do tempo. Na rede, "Você" é sempre o
 * índice `you`, e ele vai no PRIMEIRO cartão — o da esquerda — sempre. Posição
 * fixa é o que faz o olho aprender onde olhar.
 */
export function dadosDoPlacar(
  resumo: MatchSummary,
  /** `you` nulo é o ESPECTADOR: em rede, mas sem ser nenhum dos dois. */
  net: {
    you: 0 | 1 | null
    opponent: string
    /** Apelidos, na ordem dos índices. Quem tem um é chamado por ele; o endereço fica embaixo. */
    names?: [string | null, string | null] | undefined
    players?: [string, string] | undefined
  } | null,
): [JogadorDoPlacar, JogadorDoPlacar] {
  /*
   * Há um "você" na mesa? Só quando este cliente JOGA. O espectador está em
   * rede e não é ninguém; para ele os dois cartões dizem "Jogador 1" e
   * "Jogador 2", como no treino — chamar um deles de "você" seria mentira o
   * tempo todo, não só metade dele.
   */
  const jogo = net !== null && net.you !== null
  const jogador = (i: 0 | 1): JogadorDoPlacar => {
    const eu = jogo && net.you === i
    /*
     * O nome: "Você" para mim; para o outro (e para os dois, quando assisto),
     * o APELIDO quando há um, senão o rótulo de sempre. O endereço abreviado
     * vai embaixo em todo caso que não é "você" — apelido não é único, e o
     * endereço é o que diz quem é.
     */
    const apelido = net?.names?.[i] ?? null
    const nome = jogo
      ? eu
        ? t('placar.voce')
        : (apelido ?? t('placar.adversario'))
      : (apelido ?? (i === 0 ? t('placar.jogador1') : t('placar.jogador2')))
    const carteira = jogo && !eu ? net.opponent : (net?.players?.[i] ?? null)
    const sub = !eu && carteira ? enderecoCurto(carteira) : null

    let bolas: number[] | null = null
    let numero = ''
    let legenda = ''

    if (resumo.statusCode?.code === 'disputa') {
      // Antes da quebra não há grupo nem ponto: o que está em jogo é a saída.
      legenda = t('placar.naDisputa')
    } else if (resumo.score) {
      // Modalidade que pontua: o placar É o número. A sinuca não tem grupo.
      numero = String(resumo.score[i])
      legenda = t('placar.pontos', { count: resumo.score[i] })
    } else if (resumo.suas) {
      bolas = resumo.suas[i]
      numero = String(bolas.length)
      legenda = bolas.length === 0 ? t('placar.soA8') : t('placar.faltam', { count: bolas.length })
    } else {
      // 8-Ball antes da quebra decidir os grupos: inventar um seria pior que
      // não mostrar nada.
      legenda = t('placar.mesaAberta')
    }

    return {
      nome,
      sub,
      bolas,
      numero,
      legenda,
      naVez: !resumo.finished && resumo.turn === i,
      venceu: resumo.finished && resumo.winner === i,
      voce: net === null || eu,
    }
  }

  // "Você" à esquerda, sempre. No treino — e para quem assiste — é o jogador 1.
  const esquerda: 0 | 1 = net?.you ?? 0
  const direita: 0 | 1 = esquerda === 0 ? 1 : 0
  return [jogador(esquerda), jogador(direita)]
}

// ----------------------------------------------------------------- layout

export type Caixa = { x: number; y: number; w: number; h: number }

/** O ponto cai dentro da caixa, bordas inclusas? */
export function dentroDaCaixa(p: { x: number; y: number }, c: Caixa): boolean {
  return p.x >= c.x && p.x <= c.x + c.w && p.y >= c.y && p.y <= c.y + c.h
}

export type EntradaDoPlacar = {
  /** Largura do canvas, em pixels de buffer. */
  larguraPx: number
  /** Pixels de buffer por pixel CSS. Todo texto passa por ele. */
  dpr: number
  orientacao: 'retrato' | 'paisagem'
  /** O pano: canto superior esquerdo e tamanho, em pixels de buffer. */
  offsetX: number
  offsetY: number
  tableWidthPx: number
  tableHeightPx: number
  /** A moldura de madeira, em pixels de buffer. */
  margem: number
  /** As faixas do HUD acima e abaixo da moldura, em pixels de buffer. */
  hudTop: number
  hudBottom: number
}

export type DisposicaoDoPlacar = {
  /** Cartão de duas linhas (nome + bolas) ou de uma só, na faixa compacta. */
  linhas: 1 | 2
  /** Os dois cartões: esquerda e direita. */
  cartoes: [Caixa, Caixa]
  /**
   * Onde o status da partida ("Mesa aberta", "Bola da vez: verde") cabe.
   *
   * No CENTRO entre os cartões quando sobra espaço (desktop); EMBAIXO da mesa
   * quando os cartões tomam a largura (celular em pé).
   */
  status: { x: number; y: number; onde: 'centro' | 'embaixo' }
  /** Tamanhos de fonte, já em pixels de buffer. */
  fonte: { nome: number; sub: number; numero: number; legenda: number }
  /** Raio de cada bolinha da fila, em pixels de buffer. */
  raioBola: number
  /** Espaço entre bolinhas. */
  entreBolas: number
  /** Recuo interno do cartão. */
  recuo: number
  /** Diâmetro do retrato redondo do jogador, na ponta de fora do cartão. */
  avatar: number
}

/**
 * Altura mínima da faixa, em pixels CSS, para o cartão ter duas linhas.
 *
 * Abaixo disso é um celular deitado com a faixa encolhida (`disposicao.ts`
 * clampa em 26px), e nome, número e bolas dividem uma linha só.
 */
export const FAIXA_PARA_DUAS_LINHAS_CSS = 48

/** Largura máxima de um cartão, em pixels CSS. Mais largo que isso vira faixa vazia. */
const CARTAO_MAXIMO_CSS = 300

/** Espaço no centro que o status precisa para caber entre os cartões. */
const CENTRO_MINIMO_CSS = 150

/** Quantas bolinhas a fila precisa acomodar: o grupo inteiro do 8-Ball. */
const BOLAS_NA_FILA = 7

export function disporPlacar(e: EntradaDoPlacar): DisposicaoDoPlacar {
  const ui = (css: number) => css * e.dpr

  /*
   * A faixa é o espaço entre o topo do HUD e a madeira. Fica um respiro de
   * 4px acima da moldura para o cartão não parecer colado no trilho.
   */
  const faixaTopo = e.offsetY - e.margem - e.hudTop
  const faixaAltura = Math.max(ui(20), e.hudTop - ui(4))
  const linhas: 1 | 2 = faixaAltura / e.dpr >= FAIXA_PARA_DUAS_LINHAS_CSS ? 2 : 1

  /*
   * Fontes em pixels CSS via dpr — é o que faz "14px" valer 14px na tela do
   * celular, e não 7. Na faixa compacta tudo encolhe um degrau.
   */
  const fonte =
    linhas === 2
      ? { nome: ui(14), sub: ui(10), numero: ui(22), legenda: ui(10) }
      : { nome: ui(12), sub: ui(9), numero: ui(15), legenda: ui(9) }
  const recuo = ui(linhas === 2 ? 10 : 8)
  const entreBolas = ui(3)

  /*
   * Largura disponível e âncoras.
   *
   * Em PAISAGEM os cartões se encostam nas bordas da moldura — é a mesa que
   * o olho usa como referência, não o canvas, que no desktop tem faixas
   * vazias dos dois lados. Em RETRATO a mesa já toma a largura toda, e os
   * cartões usam o canvas inteiro com uma margem mínima.
   */
  const gutter = ui(8)
  const esquerdaLimite = e.orientacao === 'paisagem' ? e.offsetX - e.margem : gutter
  const direitaLimite =
    e.orientacao === 'paisagem' ? e.offsetX + e.tableWidthPx + e.margem : e.larguraPx - gutter
  const disponivel = direitaLimite - esquerdaLimite

  // Com centro livre para o status, ou os dois cartões dividindo tudo.
  let larguraCartao = Math.min(ui(CARTAO_MAXIMO_CSS), (disponivel - ui(CENTRO_MINIMO_CSS)) / 2)
  let statusNoCentro = true
  if (larguraCartao < ui(140) || e.orientacao === 'retrato') {
    larguraCartao = (disponivel - gutter) / 2
    statusNoCentro = false
  }

  /*
   * As bolinhas cabem no cartão? Sete bolas de raio 7 com 3px entre elas
   * pedem 116px CSS mais o recuo. Se o cartão for mais estreito que isso —
   * um celular pequeno em pé —, a bola encolhe até caber, com piso de 4px
   * para continuar reconhecível como bola.
   */
  /*
   * O retrato redondo do jogador ocupa a ponta de fora do cartão, na altura
   * dele (com um respiro), e o resto — nome, bolas — começa depois dele. No
   * cartão estreito (celular em pé) ele encolhe: a fila de bolas vem antes.
   */
  /*
   * Nunca negativo: num cartão mais estreito que o dobro do recuo (uma tela
   * absurdamente pequena) as duas últimas contas dão abaixo de zero, e
   * `ctx.arc` com raio negativo LANÇA `IndexSizeError` — derrubando o quadro,
   * e todos os seguintes. Zero só apaga o retrato; negativo apaga o jogo.
   */
  const avatar = Math.max(
    0,
    Math.min(ui(40), faixaAltura - (linhas === 2 ? recuo : ui(6)), (larguraCartao - 2 * recuo) * 0.2),
  )
  const larguraUtil = larguraCartao - 2 * recuo - avatar - recuo / 2
  const raioIdeal = ui(linhas === 2 ? 7 : 5)
  const raioQueCabe = (larguraUtil - (BOLAS_NA_FILA - 1) * entreBolas) / (2 * BOLAS_NA_FILA)
  const raioBola = Math.max(ui(4), Math.min(raioIdeal, raioQueCabe))

  const cartoes: [Caixa, Caixa] = [
    { x: esquerdaLimite, y: faixaTopo, w: larguraCartao, h: faixaAltura },
    { x: direitaLimite - larguraCartao, y: faixaTopo, w: larguraCartao, h: faixaAltura },
  ]

  const centroX = e.offsetX + e.tableWidthPx / 2
  const status = statusNoCentro
    ? { x: centroX, y: faixaTopo + faixaAltura / 2, onde: 'centro' as const }
    : {
        x: centroX,
        // Na faixa de baixo, acima da dica — que fica a 38px da moldura.
        y: e.offsetY + e.tableHeightPx + e.margem + ui(14),
        onde: 'embaixo' as const,
      }

  return { linhas, cartoes, status, fonte, raioBola, entreBolas, recuo, avatar }
}
